import { Column, flexRender, Table as ReactTable, RowData } from '@tanstack/react-table';
import { useState } from 'react';
import { Dialog, DialogTrigger, Popover, PopoverProps } from 'react-aria-components';
import { Icon } from '../atoms';
import { Button, IconButton, Input } from '../molecules';
import { DatePicker } from './DatePicker';
import cx from 'classix';

type TableProps<T> = {
  table: ReactTable<T>;
  isSimple?: boolean;
};

declare module '@tanstack/react-table' {
  //allows us to define custom properties for our columns
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'date-select';
  }
}

/**
 * A simple table component that uses the @tanstack/react-table library with predefined styling
 * For more complex tables and deeper customization, use the @tanstack/react-table library directly
 */
export const Table = <T,>(props: TableProps<T>) => {
  const { table, isSimple = false } = props;

  return (
    <div className={`overflow-x-auto flex flex-col`}>
      <table className='text-left text-xs text-gray-500'>
        <thead className={cx('text-xs  ', isSimple ? '' : 'text-gray-700 uppercase bg-gray-50')}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={cx(
                    `align-top ${header.column.getIsResizing() ? 'select-none' : ''}`,
                    isSimple ? 'px-2 py-1' : 'border px-4 py-3',
                  )}
                  style={{ width: `${header.getSize()}px` }}
                >
                  <div
                    className={
                      'text-nowrap whitespace-nowrap ' +
                      (header.column.getCanSort() ?
                        'select-none cursor-pointer flex items-center gap-1'
                      : '')
                    }
                    onClick={
                      header.column.getCanSort() ?
                        header.column.getToggleSortingHandler()
                      : undefined
                    }
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {
                      {
                        asc: <Icon type='arrow-up' size={'16'} />,
                        desc: <Icon type='arrow-down' size={'16'} />,
                      }[header.column.getIsSorted() as string] ?? (
                        <div className='inline h-4 w-4' />
                      ) /** This empty div keeps a placeholder the same size as the sort icon so that the column header doesn't resize when turning on sort */
                    }
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      onDoubleClick={() => header.column.resetSize()}
                      className='absolute top-0 right-0 h-full w-[5px] hover:bg-gray-300 cursor-col-resize'
                    />
                  </div>
                  {header.column.getCanFilter() && table.options.manualFiltering && (
                    <Filter column={header.column as Column<unknown, unknown>} />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={cx('odd:bg-white even:bg-gray-50', isSimple ? 'border-b' : '')}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={cx(' ', isSimple ? 'px-1 py-0.5' : 'border px-2 py-1')}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className='flex justify-between items-center p-4 gap-4'>
        <div />
        <div className='flex center items-center gap-4 ml-8 text-xs'>
          <Button
            variant='link-color'
            size='sm'
            onPress={() => table.previousPage()}
            isDisabled={!table.getCanPreviousPage()}
          >
            {'<'}
          </Button>
          {table.getState().pagination.pageIndex + 1} of {Math.max(table.getPageCount(), 1)}
          <Button
            variant='tertiary-color'
            size='sm'
            onPress={() => table.nextPage()}
            isDisabled={!table.getCanNextPage()}
          >
            {'>'}
          </Button>
        </div>
        <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}
          className='text-xs'
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const Filter = ({ column }: { column: Column<RowData, unknown> }) => {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant } = column.columnDef.meta ?? {};
  return filterVariant === 'text' ?
      <Input
        type='inline'
        value={(columnFilterValue as string) || ''}
        onChange={(e) => column.setFilterValue(e.target.value)}
        placeholder={`Search`}
        aria-label={`${column.id} filter`}
      />
    : <DateRangePopover column={column} />;
};

const DateRangePopover = (
  props: PopoverProps & {
    column: Column<RowData, unknown>;
  },
) => {
  const { column } = props;
  const columnFilterValue = column.getFilterValue() as [string | undefined, string | undefined];
  const [startDate, setStartDate] = useState<string | undefined>(columnFilterValue?.[0]);
  const [endDate, setEndDate] = useState<string | undefined>(columnFilterValue?.[1]);
  const handleStartDateChange = (value: Date) => {
    const valueAsString = value.toISOString().split('T')[0];
    setStartDate(valueAsString);
    column.setFilterValue([valueAsString, endDate]);
  };
  const handleEndDateChange = (value: Date) => {
    const valueAsString = value.toISOString().split('T')[0];
    setEndDate(valueAsString);
    column.setFilterValue([startDate, valueAsString]);
  };
  const handleClearFilter = () => {
    column.setFilterValue(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
  };
  return (
    <div className='flex gap-4 mt-[2px] max-w-fit mx-0 px-0'>
      <DialogTrigger>
        <IconButton
          size='md'
          icon='calendar-check-01'
          variant={startDate || endDate ? 'primary' : 'secondary-color'}
          aria-label='apply filter'
        />
        <Popover>
          <Dialog>
            <div className='flex flex-col gap-2 border justify-self-center bg-white p-2 rounded-lg shadow-md w-min'>
              <DatePicker
                initialDate={startDate ? new Date(startDate) : new Date()}
                maxDate={new Date(endDate ?? new Date())}
                onChange={handleStartDateChange}
                label='Start Range'
                aria-label='start range date'
              />
              <DatePicker
                minDate={startDate ? new Date(startDate) : new Date()}
                maxDate={new Date()}
                initialDate={endDate ? new Date(endDate) : new Date()}
                onChange={handleEndDateChange}
                label='End Range'
                aria-label='end range date'
              />
            </div>
          </Dialog>
        </Popover>
      </DialogTrigger>
      {(startDate || endDate) && (
        <Button
          variant='tertiary-danger'
          size='sm'
          onPress={handleClearFilter}
          aria-label='clear filter'
        >
          clear
        </Button>
      )}
    </div>
  );
};
